import React, { RefObject, useState, useEffect, useLayoutEffect, useRef } from 'react';
import useUser from '../../../../hooks/user';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import ModalRegionalDre from '../../../../components/modal/contac/regional-dre/ModalRegionalDre';
import FiltroModal from '../../../../components/modal/contac/filtro/ModalFiltro';
import { fetchRegionaisResumoLista, fetchRegionalResumo, fetchContacStatus } from '../../../../api/contac/contacAnalyticsApi';
import { fetchRegionais } from '../../../../api/contac/contacRegionaisApi';
import { notify } from '../../../../shared/feedback/notify';
import ArrowLeftOrange from '../../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../../img/arrow-right-gray.svg';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import { FixedSizeList as List } from 'react-window';
import Filter from '../../../../img/filter.svg';
import { DismissableTip } from '../../../../components/tooltip/DismissableTip';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
import './DashboardRegional.css';
import '../../../../components/modal/contac/placa-dre/ModalPlacaDre.css';
import '../../../../components/modal/contac/categoria-dre/ModalCategoriaDre.css';
import { removerAcentos } from '../../../../shared/texto/busca-sem-acento';

interface LinhaCompetencia {
  ordem: number;
  linha: 'TOTAL' | 'CLASSIFICACAO';
  regional: string;
  competencia: string;
  classificacao: string | null;
  total_entradas: number;
  total_saidas: number;
  saldo: number;
}

const classBySign3 = (n?: number) =>
  typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3';

const formatCurrency = (valor: number | null | undefined) =>
  typeof valor === 'number'
    ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : '0,00';

const formatPercent = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : '—';

const pctOf = (signedValue: number, baseAbs: number) =>
  baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;

const norm = (v: any) => removerAcentos(String(v ?? '').trim().toUpperCase());

const buildClassificacoesRegional = (
  competencias: Record<string, LinhaCompetencia[]> | undefined,
  tipo: 'ENTRADA' | 'SAIDA',
) => {
  const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
  for (const [mes, linhas] of Object.entries(competencias ?? {})) {
    if (!/^\d{2}\/\d{4}$/.test(mes)) continue;
    for (const l of linhas ?? []) {
      if (l?.linha !== 'CLASSIFICACAO') continue;
      const key = (l?.classificacao ?? '').trim() || 'Sem Classificação';
      const v = tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
      if (v === 0) continue;
      if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
      acc[key].total += v;
      acc[key].porMes[mes] = (acc[key].porMes[mes] ?? 0) + v;
    }
  }
  return Object.values(acc)
    .map((x) => ({
      classificacao: x.classificacao,
      total: x.total,
      detalhes: Object.entries(x.porMes).map(([competencia, valor]) => ({ mes: competencia, valor })),
    }))
    .sort((a, b) => b.total - a.total);
};

interface DashboardRegionalProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

const DashboardRegional: React.FC<DashboardRegionalProps> = ({ sidebarRef }) => {
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<string | null>(null);
  const [statusIndisponivel, setStatusIndisponivel] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Sem o carimbo de atualizacao o usuario le dado antigo achando que e o mais recente:
    // o aviso fica na propria tela (permanente) alem do toast, que some em segundos.
    fetchContacStatus()
      .then((s) => setUltimaAtualizacao(s.ultimaAtualizacao))
      .catch(() => {
        setStatusIndisponivel(true);
        notify.warning('Não foi possível verificar a data da última atualização dos dados.', { toastId: 'contac-status' });
      });
  }, []);

  const user = useUser();
  /**
   * at-69 (BUG-V71): a visão é decidida pelo PAPEL, nunca pela presença do código.
   *
   * O backend isola os dados do Contac por papel (`REGIONAL` vê só a própria regional;
   * `CONTAC` vê tudo). O `|| !!user?.codigoRegional` que existia aqui fazia o frontend
   * se auto-restringir por um critério diferente: um usuário `CONTAC` (ou `ADMIN`) com o
   * campo `codigoRegional` preenchido no cadastro perdia a visão consolidada e passava a
   * ver "Sua Carteira" de uma única regional — sem erro, sem aviso, e com o backend
   * pronto para entregar o consolidado.
   */
  const isRegionalUser = user?.userType === 'REGIONAL';
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const codigoRegional = user?.codigoRegional;

  const [dreResultado, setDreResultado] = useState<any | null>(null);
  const [dreExpanded, setDreExpanded] = useState<string[]>([]);

  const toggleExpandDre = (key: string) =>
    setDreExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const [, setListWidth] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  useLayoutEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setListWidth(el.clientWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [dataRegionaisTodas, setDataRegionaisTodas] = useState<
    { name: string; receitaTotal: number; saidaTotal: number; Resultado: number }[]
  >([]);

  const [sortColumn, setSortColumn] = useState<'name' | 'receitaTotal' | 'saidaTotal' | 'Resultado' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const defaultDate = new Date();
  defaultDate.setDate(1);
  defaultDate.setMonth(defaultDate.getMonth() - 2);
  const defaultYear = defaultDate.getFullYear();
  const defaultMonth = defaultDate.getMonth() + 1;

  const PAGE_SIZE_OPTIONS = [10, 20, 30, 50] as const;
  const PAGE_SIZE_ALL = 600;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_ALL);
  const [limitUi, setLimitUi] = useState(PAGE_SIZE_ALL);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasFiltered, setHasFiltered] = useState(false);

  const [periodoSelecionado, setPeriodoSelecionado] = useState<{ ini: string; fim: string } | null>(null);

  const [startYear, setStartYear] = useState<number>(defaultYear);
  const [startMonth, setStartMonth] = useState<number>(defaultMonth);
  const [endYear, setEndYear] = useState<number>(defaultYear);
  const [endMonth, setEndMonth] = useState<number>(defaultMonth);

  const [modalOpen, setModalOpen] = useState(false);
  const [regionalSelecionada, setRegionalSelecionada] = useState<{ nome: string, codigo: number, baseTag?: string } | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Mapeamento de nome da regional para o código e baseTag
  const [regionaisMap, setRegionaisMap] = useState<Record<string, { codigo: number, baseTag: string }>>({});
  // at-64 (BUG-V80): distingue "esta regional não tem código cadastrado" de "a lista de
  // regionais não carregou". As duas levavam à mesma mensagem, que mandava o usuário
  // investigar o cadastro da regional quando o problema era da tela.
  const [mapaRegionaisFalhou, setMapaRegionaisFalhou] = useState(false);

  const formatPeriodo = (ini: string, fim: string): string => {
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    const [yi, mi] = ini.split('-').map(Number);
    const [yf, mf] = fim.split('-').map(Number);
    return `${meses[mi - 1]}/${yi} – ${meses[mf - 1]}/${yf}`;
  };

  const periodoExibido =
    hasFiltered && periodoSelecionado ? formatPeriodo(periodoSelecionado.ini, periodoSelecionado.fim) : '';

  const applyFilters = (sY: number, sM: number, eY: number, eM: number) => {
    const dataInicio = new Date(sY, sM - 1, 1).toISOString().split('T')[0];
    const dataFim = new Date(eY, eM, 0).toISOString().split('T')[0];

    setLimit(limitUi);
    setPage(1);
    setHasFiltered(true);
    setPeriodoSelecionado({ ini: dataInicio, fim: dataFim });
    loadData(dataInicio, dataFim, 1, limitUi);

    setStartYear(sY); setStartMonth(sM);
    setEndYear(eY); setEndMonth(eM);
  };

  // Carrega mapeamento de regionais no mount
  useEffect(() => {
    const loadRegionaisMapping = async () => {
      try {
        const resp = await fetchRegionais(1, 100);
        const mapping: Record<string, { codigo: number, baseTag: string }> = {};
        if (Array.isArray(resp?.data)) {
          resp.data.forEach((r: any) => {
            if (r.nome && r.codigoRegional) {
              mapping[norm(r.nome)] = {
                codigo: Number(r.codigoRegional),
                baseTag: r.base_tag || '',
              };
            }
          });
        }
        setRegionaisMap(mapping);
        setMapaRegionaisFalhou(false);
      } catch (err) {
        // Sem este mapeamento o clique na regional so avisa "codigo nao disponivel",
        // o que parece problema daquela regional e nao falha de carga da tela inteira.
        console.error('Erro ao mapear regionais:', err);
        // at-64: o toast da carga some; o clique acontece minutos depois. Sem guardar que
        // a carga falhou, a mensagem do clique culpa a regional por uma falha da tela.
        setMapaRegionaisFalhou(true);
        notify.errorFrom(err, 'Não foi possível carregar a lista de regionais. Recarregue a página; se persistir, acione a TI.');
      }
    };
    loadRegionaisMapping();
  }, []);

  useEffect(() => {
    if (!hasFiltered || !periodoSelecionado) return;
    loadData(periodoSelecionado.ini, periodoSelecionado.fim, page, limit);
    // Este efeito existe só para reagir à troca de página e de itens por página.
    // A troca de período já chama loadData direto em applyFilters: incluir
    // periodoSelecionado aqui faria a mesma consulta rodar duas vezes, e incluir
    // loadData (recriada a cada render) faria a consulta rodar em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  useEffect(() => {
    if (isRegionalUser && !hasFiltered) {
      applyFilters(startYear, startMonth, endYear, endMonth);
    }
    // Carga inicial da própria regional do usuário: deve rodar uma única vez,
    // quando se sabe que o usuário é regional. As datas e o applyFilters são
    // recriados a cada render; incluí-los faria o efeito refazer a consulta a
    // cada mudança de filtro, atropelando o período escolhido pelo usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegionalUser]);

  const [dataResultado, setDataResultado] = useState<{
    totalReceita: number;
    totalSaidas: number;
    resultado: number;
  } | null>(null);

  const handleSort = (column: typeof sortColumn) => {
    if (!column) return;

    let newDirection: 'asc' | 'desc' = 'desc';
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(column);
    setSortDirection(newDirection);

    const sorted = [...dataRegionaisTodas].sort((a, b) => {
      const valA = a[column];
      const valB = b[column];
      if (typeof valA === 'number' && typeof valB === 'number') {
        return newDirection === 'asc' ? valA - valB : valB - valA;
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return newDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return 0;
    });

    setDataRegionaisTodas(sorted);
  };

  const formatarValor = (valor: number) => {
    const formatado = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
    return valor < 0 ? `R$ -${formatado.replace('R$', '').trim()}` : formatado;
  };

  const loadData = async (
    dataInicio: string,
    dataFim: string,
    pageArg = page,
    limitArg = limit,
  ) => {
    try {
      setLoading(true);

      if (isRegionalUser) {
        const resp = await fetchRegionalResumo({
          dataInicio,
          dataFim,
          codigo_regional: codigoRegional ? Number(codigoRegional) : undefined,
        });

        const d = resp?.detalhamento || {};
        const competencias = d as Record<string, any>;

        const receitaBruta = toNum(d.totaisEntrada);
        const despesasOperacionais = toNum(d.totaisSaida);
        const resultadoFinal = toNum(d.resultado);

        const entradasOp = buildClassificacoesRegional(competencias, 'ENTRADA');
        const saidasOp = buildClassificacoesRegional(competencias, 'SAIDA');

        setDreResultado({
          regional: resp.regional || '',
          codigoRegional: String(codigoRegional),
          receitaBruta,
          despesasOperacionais,
          resultadoFinal,
          entradasClassificacoes: entradasOp,
          saidasClassificacoes: saidasOp,
        });

      } else {
        const resp = await fetchRegionaisResumoLista({
          dataInicio,
          dataFim,
          order: 'lucro',
          page: pageArg,
          limit: limitArg,
        });

        const num = (x: any) => (typeof x === 'number' ? x : Number(x ?? 0)) || 0;
        const items = Array.isArray(resp?.items) ? resp.items : [];

        const totalReceita = items.reduce((acc: number, it: any) => acc + num(it?.totaisEntrada), 0);
        const totalSaidas = items.reduce((acc: number, it: any) => acc + num(it?.totaisSaida), 0);
        const resultado = items.reduce((acc: number, it: any) => acc + num(it?.resultado), 0);

        setDataResultado({
          totalReceita,
          totalSaidas,
          resultado,
        });

        const regionais = items.map((it: any) => ({
          name: it?.regional || 'Sem regional',
          receitaTotal: num(it?.totaisEntrada),
          saidaTotal: num(it?.totaisSaida),
          Resultado: num(it?.resultado),
        }));

        setDataRegionaisTodas(regionais);

        const totalItems = Number(resp?.total) || 0;
        const totalPages = Math.max(1, Math.ceil(totalItems / (limitArg || 1)));

        setTotalPages(totalPages);
        setTotalItems(totalItems);
      }
    } catch (error) {
      notify.errorFrom(
        error,
        'Não foi possível carregar os dados das regionais. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setLoading(false);
    }
  };

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  if (loading) return <FullScreenLoader />;

  const baseRbAbs = Math.abs(Number(dreResultado?.receitaBruta) || 0);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: isRegionalUser ? (
        <p>
          Esta é a <strong>sua carteira</strong>: o desempenho financeiro da sua regional — receitas, despesas e
          resultado — no período escolhido. Você vê apenas os dados da sua regional; o próprio sistema faz esse
          recorte a partir do código de regional do seu cadastro. Ao abrir, a tela já carrega o período mais
          recente disponível.
        </p>
      ) : (
        <p>
          O desempenho financeiro consolidado <strong>por regional</strong>: os cartões <strong>Entradas</strong>,{' '}
          <strong>Saídas</strong> e <strong>Resultado</strong> somam o período inteiro, e a tabela detalha regional
          a regional. Os dados vêm do sistema financeiro, consolidados por mês — esta tela só consulta.
        </p>
      ),
    },
    {
      titulo: 'Período de análise',
      conteudo: (
        <p>
          O período é escolhido por mês e ano em <strong>Filtros</strong>. A consulta começa em{' '}
          <strong>janeiro de 2025</strong> e vai até o <strong>fechamento de dois meses atrás</strong> — o mês
          corrente e o anterior ainda não estão consolidados e por isso não podem ser selecionados.
        </p>
      ),
    },
    isRegionalUser
      ? {
          titulo: 'Ler o Demonstrativo de Resultados',
          conteudo: (
            <>
              <p>
                <strong>Receita Total (Entradas)</strong> e <strong>Despesa Total (Saídas)</strong> se expandem em
                classificações e, dentro delas, em meses — clique nas setas para abrir cada nível. A coluna{' '}
                <strong>% Rec. Bruta</strong> mostra o peso de cada linha sobre a receita total do período.
              </p>
              <p>
                <strong>Resultado (Receita - Despesa)</strong> é o saldo final da sua regional no período. Valores
                aparecem em verde quando positivos e em vermelho quando negativos.
              </p>
            </>
          ),
        }
      : {
          titulo: 'A tabela e o detalhamento',
          conteudo: (
            <p>
              Os cabeçalhos <strong>Regional</strong>, <strong>Entradas</strong>, <strong>Saídas</strong> e{' '}
              <strong>Resultado</strong> ordenam a lista (a ordenação vale sobre a página carregada — use{' '}
              <strong>Regionais por página: Todas</strong> para ordenar o conjunto completo). Clicar no{' '}
              <strong>nome da regional</strong> abre o Demonstrativo de Resultados dela, com receitas e despesas
              por classificação e por mês, além das placas ativas e do período.
            </p>
          ),
        },
  ];

  return (
    <div className="Frame1000004012 dashboard-regional-page">
      <div className="ConteDo">
        {/* Título */}
        <div className="TTuloRecepcao">
          <div className="ChefeDeOficina">
            {isRegionalUser ? 'Contac - Sua Carteira' : 'Contac - Dashboard de Regionais'}
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            >
              ?
            </button>
            {periodoExibido && <span className="PeriodoTitulo"> {periodoExibido}</span>}
          </div>
          {ultimaAtualizacao ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-3, #888)', margin: '4px 0 0 0' }}>
              Dados atualizados em: {new Date(ultimaAtualizacao).toLocaleString('pt-BR')}
            </p>
          ) : statusIndisponivel ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-laranja, #b26a00)', margin: '4px 0 0 0' }}>
              Não foi possível verificar a data da última atualização dos dados.
            </p>
          ) : null}
        </div>

        <div className="VisualizarOrdenar">
          <div
            className="Frame1000004037"
            style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}
          >
            <DismissableTip
              tipId="contac_regional_filtros"
              content="Selecione o período desejado para filtrar os dados das regionais."
              position="bottom"
            >
              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">Filtros</div>
                </div>
              </div>
            </DismissableTip>
          </div>
        </div>

        {/* ====== Bloco de Resumo Financeiro (acima da tabela) ====== */}
        {!isRegionalUser && dataResultado && (
          <div className="container-superior-regional">
            <div className="Frame1-regional">
              {/* Entradas */}
              <div className="card-financeiro-regional">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_arrow_down.svg').default} alt="Seta para baixo" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="card-titulo">Entradas</div>
                  <div className="card-valor">{formatarValor(dataResultado?.totalReceita ?? 0)}</div>
                </div>
              </div>

              {/* Saídas */}
              <div className="card-financeiro-regional">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_arrow_up.svg').default} alt="Seta para cima" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="card-titulo">Saídas</div>
                  <div className="card-valor">{formatarValor(dataResultado?.totalSaidas ?? 0)}</div>
                </div>
              </div>

              {/* Resultado */}
              <div className="card-financeiro-regional resultado">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_line.svg').default} alt="Linha" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="card-titulo">Resultado</div>
                  <div
                    className="card-valor"
                    style={{
                      color:
                        (dataResultado?.resultado ?? 0) > 0
                          ? 'green'
                          : (dataResultado?.resultado ?? 0) < 0
                            ? 'red'
                            : 'black',
                    }}
                  >
                    {formatarValor(dataResultado?.resultado ?? 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isRegionalUser ? (
          <div className="inline-dre-body">
            {!dreResultado ? (
              <div className="card">
                <div className="card-entry">Nenhum dado encontrado para esta regional nesse período.</div>
              </div>
            ) : (
              <>
                {/* Card 1 - Suas Informações */}
                <div className="card card-left">
                  <div className="modal-header">Suas informações</div>
                  <div className="info-row">
                    <span className="info-label">Regional</span>
                    <span className="info-value">{dreResultado.regional}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Código</span>
                    <span className="info-value">{String(dreResultado.codigoRegional || codigoRegional)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Período</span>
                    <span className="info-value">
                      {periodoSelecionado ? formatPeriodo(periodoSelecionado.ini, periodoSelecionado.fim) : '—'}
                    </span>
                  </div>
                </div>

                {/* Card 2 - DRE */}
                <div className="card">
                  <div className="modal-header">Demonstrativo de Resultados (DRE)</div>
                  <div className="row-between dre-columns-header">
                    <div />
                    <div className="dre-value-pct">
                      <span className="dre-col-header" style={{ minWidth: 140, textAlign: 'right' }}>Valor</span>
                      <span className="dre-col-header" style={{ minWidth: 80, textAlign: 'right' }}>% Rec. Bruta</span>
                    </div>
                  </div>

                  {/* Receita Total (Entradas) */}
                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpandDre('ent')}
                    aria-expanded={dreExpanded.includes('ent')}
                  >
                    <div className="dre-title">
                      Receita Total <span className="dre-note">(Entradas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={dreExpanded.includes('ent') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-positive3">R$ {formatCurrency(dreResultado?.receitaBruta)}</span>
                      <span className="dre-percent value-positive5">
                        {formatPercent(pctOf(Math.abs(Number(dreResultado?.receitaBruta) || 0), baseRbAbs))}
                      </span>
                    </div>
                  </div>

                  {dreExpanded.includes('ent') && (
                    <div className="subclass-container">
                      {(dreResultado?.entradasClassificacoes || []).map((ent: any, idx: number) => (
                        <React.Fragment key={`ent-op-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{ent.classificacao}</span>
                              <img
                                src={dreExpanded.includes(`ent-op-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpandDre(`ent-op-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {dreExpanded.includes(`ent-op-${idx}`) &&
                            (ent.detalhes || []).map((det: any, dIdx: number) => (
                              <div className="detail-row" key={`ent-op-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Despesa Total (Saídas) */}
                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpandDre('sai')}
                    aria-expanded={dreExpanded.includes('sai')}
                  >
                    <div className="dre-title">
                      Despesa Total <span className="dre-note">(Saídas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={dreExpanded.includes('sai') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-negative3">
                        R$ -{formatCurrency(dreResultado?.despesasOperacionais)}
                      </span>
                      <span className="dre-percent value-negative5">
                        {formatPercent(pctOf(-Math.abs(Number(dreResultado?.despesasOperacionais) || 0), baseRbAbs))}
                      </span>
                    </div>
                  </div>

                  {dreExpanded.includes('sai') && (
                    <div className="subclass-container">
                      {(dreResultado?.saidasClassificacoes || []).map((sai: any, idx: number) => (
                        <React.Fragment key={`sai-dir-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{sai.classificacao}</span>
                              <img
                                src={dreExpanded.includes(`sai-dir-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpandDre(`sai-dir-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {dreExpanded.includes(`sai-dir-${idx}`) &&
                            (sai.detalhes || []).map((det: any, dIdx: number) => (
                              <div className="detail-row" key={`sai-dir-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Resultado Final */}
                  <div className="row-between" style={{ borderTop: '2px solid #EA5D0E', paddingTop: '20px', marginTop: '16px' }}>
                    <div className="dre-title" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      Resultado <span className="dre-note">(Receita - Despesa)</span>
                    </div>
                    <div className="dre-value-pct">
                      <span className={`entry-text ${classBySign3(dreResultado?.resultadoFinal)}`} style={{ fontSize: '20px', fontWeight: 'bold' }}>
                        R$ {formatCurrency(dreResultado?.resultadoFinal)}
                      </span>
                      <span className={`dre-percent ${(Number(dreResultado?.resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'}`} style={{ fontSize: '16px' }}>
                        {formatPercent(pctOf(Number(dreResultado?.resultadoFinal) || 0, baseRbAbs))}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className='ContacTable'>
            <div
              style={{
                display: 'flex',
                gap: 20,
                flexWrap: 'wrap',
                alignItems: 'center',
                paddingLeft: 30,
                paddingBottom: 0,
              }}
            >
              <div className="CtaBigContac2">
                <div className="Frame1000003987">
                  <div className="ComponentNameContac">Regionais por página:</div>
                  <select
                    value={limitUi}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setLimitUi(newLimit);
                      setLimit(newLimit);
                      setPage(1);
                    }}
                    className="ComponentNameImgIcon"
                  >
                    {PAGE_SIZE_OPTIONS.map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                    <option key="all" value={PAGE_SIZE_ALL}>Todas</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="TabelaHeaderScroll" ref={headerScrollRef}>
              <div className="Tabela Tabela--regionais Tabela--header">
                <div className="Item">
                  <DismissableTip
                    tipId="contac_regional_tabela_dre"
                    content="Clique no nome de uma regional para abrir a Demonstração de Resultado (DRE) detalhada dela."
                    position="top"
                    disabled={dataRegionaisTodas.length === 0}
                  >
                    <div className="CabecalhoData" onClick={() => handleSort('name')}>
                      Regional
                    </div>
                  </DismissableTip>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('receitaTotal')}>
                    Entradas
                    {sortColumn === 'receitaTotal' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('saidaTotal')}>
                    Saídas
                    {sortColumn === 'saidaTotal' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('Resultado')}>
                    Resultado
                    {sortColumn === 'Resultado' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
              </div>
            </div>

            <div className="TabelaScroll" ref={tableContainerRef} onScroll={handleScroll}>

              <List
                height={Math.max(48, 48 * dataRegionaisTodas.length)}
                itemCount={dataRegionaisTodas.length}
                itemSize={48}
                width="100%"
              >
                {({ index, style }) => {
                  const item = dataRegionaisTodas[index];
                  return (
                    <div className="Tabela Tabela--regionais" style={style} key={item.name || index}>
                      <div className="Item">
                        <div
                          className="Data link"
                          style={{ cursor: 'pointer', color: '#EA5D0E', textDecoration: 'underline' }}
                          onClick={() => {
                            const nameNorm = norm(item.name);
                            const info = regionaisMap[nameNorm];
                            if (!info) {
                              // at-64: erro de carga não pode virar afirmação sobre o cadastro.
                              return notify.warning(
                                mapaRegionaisFalhou
                                  ? 'Não foi possível consultar a lista de regionais, então o detalhamento não pode ser aberto. Recarregue a página; se persistir, acione a TI.'
                                  : 'Código da regional não disponível para esta regional.',
                              );
                            }
                            setRegionalSelecionada({ nome: item.name, codigo: info.codigo, baseTag: info.baseTag });
                            setModalOpen(true);
                          }}
                        >
                          {item.name}
                        </div>
                      </div>
                      <div className="Item"><div className="Data">{formatarValor(item.receitaTotal)}</div></div>
                      <div className="Item"><div className="Data">{formatarValor(item.saidaTotal)}</div></div>
                      <div className="Item">
                        <div
                          className="Data"
                          style={{
                            color: item.Resultado > 0 ? 'green' : item.Resultado < 0 ? 'red' : 'var(--tema-texto, black)',
                          }}
                        >
                          {formatarValor(item.Resultado)}
                        </div>
                      </div>
                    </div>
                  );
                }}
              </List>
            </div>

            {/* Paginação */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%' }}>
              {totalItems > 0 && (
                <div style={{ fontSize: '14px', color: 'var(--tema-texto-2, #666)' }}>
                  Total de {totalItems} regionais
                </div>
              )}
              <div className="PaginaO">
                <div
                  className={`Frame1000003950 ${page === 1 ? 'disabled' : ''}`}
                  onClick={() => { if (page > 1) setPage(page - 1); }}
                  style={{ cursor: page === 1 ? 'not-allowed' : 'pointer' }}
                >
                  <img
                    className="imgIcon"
                    src={page === 1 ? ArrowLeftGray : ArrowLeftOrange}
                    alt="Seta para Esquerda"
                    width="24"
                    height="24"
                  />
                  <div className="Anterior" style={{ color: page === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                    Anterior
                  </div>
                </div>

                <div className="Group21">
                  <div className="Group20">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 10;

                      pages.push(
                        <div
                          key={1}
                          className={`page-number ${page === 1 ? 'active' : ''}`}
                          onClick={() => setPage(1)}
                        >
                          1
                          {page === 1 && <div className="Ellipse14" />}
                        </div>
                      );

                      if (page > maxVisiblePages - 4) {
                        pages.push(<span key="left-ellipsis" className="ellipsis">...</span>);
                      }

                      const startPage = Math.max(
                        2,
                        Math.min(
                          page - Math.floor((maxVisiblePages - 2) / 2),
                          totalPages - (maxVisiblePages - 2)
                        )
                      );
                      const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <div
                            key={i}
                            className={`page-number ${page === i ? 'active' : ''}`}
                            onClick={() => setPage(i)}
                          >
                            {i}
                            {page === i && <div className="Ellipse14" />}
                          </div>
                        );
                      }

                      if (endPage < totalPages - 1) {
                        pages.push(<span key="right-ellipsis" className="ellipsis">...</span>);
                      }

                      if (totalPages > 1) {
                        pages.push(
                          <div
                            key={totalPages}
                            className={`page-number ${page === totalPages ? 'active' : ''}`}
                            onClick={() => setPage(totalPages)}
                          >
                            {totalPages}
                            {page === totalPages && <div className="Ellipse14" />}
                          </div>
                        );
                      }

                      return pages;
                    })()}
                  </div>
                </div>

                <div
                  className={`Frame1000003949 ${page === totalPages ? 'disabled' : ''}`}
                  onClick={() => { if (page < totalPages) setPage(page + 1); }}
                  style={{ cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
                >
                  <div className="PrXima" style={{ color: page === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                    Próxima
                  </div>
                  <img
                    className="imgIcon"
                    src={page === totalPages ? ArrowRightGray : ArrowRightOrange}
                    alt="Seta para Direita"
                    width="24"
                    height="24"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {modalOpen && regionalSelecionada && (
          <ModalRegionalDre
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            codigoRegional={regionalSelecionada.codigo}
            nomeRegional={regionalSelecionada.nome}
            baseTag={regionalSelecionada.baseTag}
            dataInicio={new Date(startYear, startMonth - 1, 1).toISOString().split('T')[0]}
            dataFim={new Date(endYear, endMonth, 0).toISOString().split('T')[0]}
          />
        )}
        <FiltroModal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          sidebarRef={sidebarRef}
          onApplyFilters={(sY, sM, eY, eM) => {
            setStartYear(sY);
            setStartMonth(sM);
            setEndYear(eY);
            setEndMonth(eM);

            const ymd = (y: number, m: number, d: number) =>
              `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

            const dataInicio = ymd(sY, sM, 1);
            const dataFim = ymd(eY, eM, new Date(eY, eM, 0).getDate());

            setLimit(limitUi);
            setPage(1);
            setHasFiltered(true);
            setPeriodoSelecionado({ ini: dataInicio, fim: dataFim });
            loadData(dataInicio, dataFim, 1, limitUi);
          }}
          initialStartYear={startYear}
          initialStartMonth={startMonth}
          initialEndYear={endYear}
          initialEndMonth={endMonth}
        />

        <ComoFunciona
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          titulo={isRegionalUser ? 'Como Funciona — Sua Carteira' : 'Como Funciona — Dashboard de Regionais'}
          secoes={helpSections}
        />
      </div>
    </div>
  );
};

export default DashboardRegional;
